import { useEffect, useState } from 'react';
import { Button, Divider, Form, Upload } from 'antd';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { getAccountBalanceApi, getSenderAccountsApi } from '@http/accounts';
import { swiftRubTransactionApi } from '@http/payments';
import { getTemplatesApi } from '@http/user';
import { getCountriesApi } from '@http/global';

import SenderAccountsModal from '@components/Payments/modals/SenderAccountsModal';
import SearchBicModal from '@components/Payments/modals/SearchBicModal';
import ConfirmPaymentModal from '@components/Payments/modals/ConfirmPaymentModal';
import Currencies from '@components/Currency/OrienbankRates';
import Templates from '@components/Templates/Templates';
import SaveTemplateModal from '@components/Payments/modals/SaveTemplateModal';

import { deadlineAC, otpConfirmAC, repeatSwiftRubTransactionAC, verifyTransIdAC } from '@store/reducers/transactions';
import { pickedSwiftRubTemplateAC } from '@store/reducers/user';
import {
  isConfirmPaymentModalOpenAC,
  isSaveTemplateModalOpenAC,
  isSearchBicModalOpenAC,
  newRubOperationTemplateAC,
  rubOperationQueryAC,
  searchedSwiftAC,
} from '@store/reducers/payments';
import { selectedSenderAccountAC, senderAccountBalanceAC, senderAccountsModalOpenAC } from '@store/reducers/accounts';
import RubOperationsForm from '@components/Payments/forms/RubOperationsForm';

import SelectCounterparty from '@components/Counterparties/SelectCounterparty';
import { UploadOutlined } from '@ant-design/icons';

function RubOperations() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const senderAccounts = useAppSelector(state => state.accountsReducer.senderAccounts);
  const pickedSwiftRubTemplate = useAppSelector(state => state.userReducer.pickedSwiftRubTemplate);
  const repeatSwiftRubTransaction = useAppSelector(state => state.transactionsReducer.repeatSwiftRubTransaction);
  const newRubOperationTemplate = useAppSelector(state => state.paymentsReducer.newRubOperationTemplate);
  const rubOperationQuery = useAppSelector(state => state.paymentsReducer.rubOperationQuery);
  const permissions = useAppSelector(state => state.userReducer.permissions);
  const senderAccountBalance = useAppSelector(state => state.accountsReducer.senderAccountBalance);

  const [senderAccountCurrency, setSenderAccountCurrency] = useState('');
  const [file, setFile] = useState<any>();

  useEffect(() => {
    initializeData();

    return clearFields;
  }, []);

  useEffect(() => {
    pickedSwiftRubTemplateHandler();
  }, [pickedSwiftRubTemplate]);

  useEffect(() => {
    repeatSwiftTransactionHandler();
  }, [repeatSwiftRubTransaction]);

  useEffect(() => {
    dispatch(otpConfirmAC(false));
  }, [rubOperationQuery]);

  const pickedSwiftRubTemplateHandler = () => {
    setSenderAccountCurrency(pickedSwiftRubTemplate.sending_ccy);

    fillQuery(pickedSwiftRubTemplate, 'template');
    fillForms(pickedSwiftRubTemplate, 'template');

    if (+pickedSwiftRubTemplate.sending_amount > 0) {
      dispatch(getAccountBalanceApi('sender', { cust_ac_no: pickedSwiftRubTemplate.sending_ac }));
    }
  };

  const repeatSwiftTransactionHandler = () => {
    setSenderAccountCurrency(repeatSwiftRubTransaction.sending_ccy);

    fillForms(repeatSwiftRubTransaction, 'repeat');
    fillQuery(repeatSwiftRubTransaction, 'repeat');

    if (+repeatSwiftRubTransaction.sending_amount > 0) {
      dispatch(getAccountBalanceApi('sender', { cust_ac_no: repeatSwiftRubTransaction.sending_ac }));
    }
  };

  const fillForms = (data: any, type: any) => {
    form.setFieldValue('sending_ac', data.sending_ac);
    form.setFieldValue('receiving_ac', data.receiving_ac);
    form.setFieldValue('receiving_ac2', data.receiving_ac);
    form.setFieldValue('receiver_name', data.receiver_name);
    form.setFieldValue('sender_address', data.sender_address);
    form.setFieldValue('receiver_country', data.receiver_country);
    form.setFieldValue('bank_code', data.bank_code);
    form.setFieldValue('receiver_bank_name', data.receiver_bank_name);
    form.setFieldValue('receiver_bank_address', data.receiver_bank_address);
    form.setFieldValue('tax_id', data.tax_id);
    form.setFieldValue('receiver_kpp', data.receiver_kpp);
    form.setFieldValue('bank_corr_acc', data.bank_corr_acc);
    form.setFieldValue('receiver_address', data.receiver_address);

    dispatch(selectedSenderAccountAC(data?.sending_ac));
  };

  const fillQuery = (data: any, type: any) => {
    dispatch(
      rubOperationQueryAC({
        sending_ac: data.sending_ac,
        receiving_ac: data.receiving_ac,
        receiving_ac2: data.receiving_ac,
        receiver_name: data.receiver_name,
        sender_address: data.sender_address,
        receiver_country: data.receiver_country,
        bank_code: data.bank_code,
        receiver_bank_name: data.receiver_bank_name,
        receiver_bank_address: data.receiver_bank_address,
        tax_id: data.tax_id,
        receiver_kpp: data.receiver_kpp,
        bank_corr_acc: data.bank_corr_acc,
        receiver_address: data.receiver_address,
      })
    );
  };

  const senderAccountSelect = (account: any) => {
    setSenderAccountCurrency(account.ccy);
    dispatch(rubOperationQueryAC({ ...rubOperationQuery, sending_ac: account.value }));

    form.setFieldValue('sending_ac', account.value);

    dispatch(senderAccountsModalOpenAC(false));
    dispatch(selectedSenderAccountAC(account));
    dispatch(getAccountBalanceApi('sender', { cust_ac_no: account.value }));
  };

  const selectBicHandler = (item: any) => {
    form.setFieldsValue({
      bank_code: item.bic_code.slice(0, 9),
      receiver_bank_name: item.bank_name,
      bank_corr_acc: item.bic_code.slice(10),
      receiver_bank_address: item.bank_address,
    });

    dispatch(isSearchBicModalOpenAC(false));
  };

  const initializeData = () => {
    dispatch(getCountriesApi());
    dispatch(searchedSwiftAC([]));
    dispatch(selectedSenderAccountAC({}));
    dispatch(senderAccountBalanceAC(0));
    dispatch(getTemplatesApi({ transaction_type: 'SWIFT_RUB' }));
    dispatch(
      getSenderAccountsApi({
        transaction_type: 'SWIFT_RUB',
      })
    );
  };

  const swiftRubTransaction = () => {
    dispatch(
      swiftRubTransactionApi({ ...rubOperationQuery, file: file?.originFileObj }, navigate, () => clearFields())
    );
  };

  const clearFields = () => {
    dispatch(rubOperationQueryAC({}));
    setSenderAccountCurrency('');

    form.resetFields();

    dispatch(otpConfirmAC(false));
    dispatch(verifyTransIdAC(''));
    dispatch(senderAccountBalanceAC(0));
    dispatch(selectedSenderAccountAC({}));
    dispatch(isConfirmPaymentModalOpenAC(false));
    dispatch(repeatSwiftRubTransactionAC({}));
    dispatch(pickedSwiftRubTemplateAC({}));
  };

  const submit = (values: any) => {
    const { file, ...query } = values;
    if (+rubOperationQuery.sending_amount > +senderAccountBalance) {
      form.setFields([
        {
          name: 'sending_ac',
          errors: [t('Insufficient funds in the account')],
        },
      ]);
    } else {
      dispatch(rubOperationQueryAC({ ...query, narrative: values?.narrative1 + ' ' + values?.narrative2 }));

      dispatch(isConfirmPaymentModalOpenAC(true));
      dispatch(deadlineAC(Date.now() + 1000 * 30));
    }
  };

  const openSaveTemplateModal = () => {
    dispatch(
      newRubOperationTemplateAC({
        ...newRubOperationTemplate,
        sending_amount: form.getFieldValue('sending_amount'),
        sending_ac: form.getFieldValue('sending_ac'),
        receiving_ac: form.getFieldValue('receiving_ac'),
        receiver_name: form.getFieldValue('receiver_name'),
        sending_ccy: senderAccountCurrency,
        narrative: form.getFieldValue('narrative'),
        doc_no: form.getFieldValue('doc_no'),
        sender_address: form.getFieldValue('sender_address'),
        receiver_country: form.getFieldValue('receiver_country'),
        receiver_bank_name: form.getFieldValue('receiver_bank_name'),
        receiver_bank_country: form.getFieldValue('receiver_bank_country'),
        receiver_address: form.getFieldValue('receiver_address'),
        tax_id: form.getFieldValue('tax_id'),
        receiver_kpp: form.getFieldValue('receiver_kpp'),
        bank_code: form.getFieldValue('bank_code'),
        receiver_bank_address: form.getFieldValue('receiver_bank_address'),
        bank_corr_acc: form.getFieldValue('bank_corr_acc'),
      })
    );

    dispatch(isSaveTemplateModalOpenAC(true));
  };

  const dummyRequest = ({ onSuccess }: any) => {
    setTimeout(() => {
      onSuccess('ok');
    }, 0);
  };

  return (
    <div className='md:grid grid-cols-6 gap-10 mt-5'>
      <div className='col-span-4'>
        {permissions?.cpty ? (
          <div>
            {permissions?.cpty[0].allow_view ? (
              <div className=' mt-7'>
                <SelectCounterparty type='SWIFT_RUB' />
              </div>
            ) : null}
          </div>
        ) : null}

        <div className=' mt-7'>
          <Templates type='SWIFT_RUB' />
        </div>
        <Divider className='my-6' />
        <RubOperationsForm form={form} senderAccountCurrency={senderAccountCurrency} />
        <Form form={form} name='Swift' onFinish={submit}>
          <p className='text-bs color-title mt-5 md:hidden'>{t('Document')}:</p>
          <div className='md:grid grid-cols-7 md:mt-7'>
            <p className='col-span-2 text-bs color-title max-md:hidden'>{t('Document')}:</p>
            <div className='col-span-5'>
              <Form.Item
                name='file'
                className='m-0'
                valuePropName='fileList'
                getValueFromEvent={e => e?.fileList}
                rules={[{ required: true, type: 'array', message: `${t('Required')}` }]}
              >
                <Upload
                  onChange={e => {
                    setFile(e.file);
                  }}
                  customRequest={dummyRequest}
                  listType='picture'
                  accept='.pdf'
                  maxCount={1}
                >
                  <Button icon={<UploadOutlined />}>{t('Upload')}</Button>
                </Upload>
              </Form.Item>
            </div>
          </div>

          <div className='flex justify-end mt-5'>
            <Button className='mr-3' type='default' onClick={openSaveTemplateModal}>
              {t('Save template')}
            </Button>
            <Button type='primary' htmlType='submit'>
              {t('Confirm')}
            </Button>
          </div>
        </Form>
        <SenderAccountsModal accounts={senderAccounts} selectHandler={senderAccountSelect} />
        <SearchBicModal selectBicHandler={selectBicHandler} />
        <SaveTemplateModal template={newRubOperationTemplate} />
        <ConfirmPaymentModal
          title={`${t('Ruble transfer')}`}
          paymentInfo={rubOperationQuery}
          currency={senderAccountCurrency}
          paymentTransactionHandler={swiftRubTransaction}
          clearFields={clearFields}
          route={'swiftRubPaymentStatus'}
          type='SWIFT_RUB'
        />
      </div>
      <div className='col-span-2 max-md:hidden'>
        <Currencies size='max-lg:text-xs text-sm' />
      </div>
    </div>
  );
}

export default RubOperations;
